/**
 * Click-to-reveal coupon promo prices (Option 3) with strict DOM toggle.
 *
 * Mobile: checkbox tick reveals price.
 * Desktop (700px+): switch button.
 *
 * Default HTML: list price + hint only — no discounted € strings.
 * Reveal ON:  discounted price injected; list price removed from DOM.
 * Reveal OFF: discounted price removed from DOM; list price restored.
 */
(function () {
  'use strict';

  const SECTION_ID = 'pcp-discount-data';
  const DESKTOP_MQ = '(min-width: 700px)';
  const discountCache = new WeakMap();
  const initialHtmlCache = new WeakMap();

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    if (Number.isNaN(cents)) return '';

    const settings = window.themeVariables?.settings || {};
    const formatString =
      format ||
      (settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat) ||
      '{{amount}}';

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision ?? 2);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands ?? ','));
      const fraction = parts[1] ? (decimal ?? '.') + parts[1] : '';
      return whole + fraction;
    }

    const match = formatString.match(placeholderRegex);
    const token = match ? match[1] : 'amount';
    let value = '';

    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function fromLabel(template, price) {
    return template ? template.replace('@@', price) : price;
  }

  function cacheInitialHtml(block) {
    if (initialHtmlCache.has(block)) return;

    const initial = block.querySelector('.pcp-block__initial');
    if (initial) initialHtmlCache.set(block, initial.innerHTML);
  }

  function clearRevealed(revealed) {
    if (!revealed) return;
    revealed.hidden = true;
    revealed.setAttribute('aria-hidden', 'true');
    revealed.replaceChildren();
  }

  function getBlockControls(block) {
    return {
      checkbox: block.querySelector('.pcp-reveal-checkbox'),
      button: block.querySelector('.pcp-reveal-btn'),
    };
  }

  function setControlsDisabled(block, disabled) {
    const { checkbox, button } = getBlockControls(block);
    if (checkbox) checkbox.disabled = disabled;
    if (button) button.disabled = disabled;
  }

  function setControlState(block, revealed) {
    const { checkbox, button } = getBlockControls(block);
    const bar = block.querySelector('.pcp-toggle-bar');

    if (checkbox) checkbox.checked = revealed;

    if (button) {
      button.setAttribute('aria-expanded', revealed ? 'true' : 'false');
      button.setAttribute('aria-checked', revealed ? 'true' : 'false');
      button.classList.toggle('pcp-reveal-btn--active', revealed);
    }

    if (bar) bar.classList.toggle('pcp-toggle-bar--active', revealed);
  }

  async function fetchDiscountData(handle) {
    if (!handle) throw new Error('Missing product handle');

    const response = await fetch(
      '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID,
      {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      }
    );

    if (!response.ok) throw new Error('Discount fetch failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const payload = doc.querySelector('.pcp-discount-payload');

    if (!payload) throw new Error('No discount payload');

    const baseCents = parseInt(payload.dataset.pcpBase, 10) || 0;
    const saleCents = parseInt(payload.dataset.pcpSale, 10) || 0;

    if (saleCents <= 0 || baseCents <= saleCents) throw new Error('Invalid discount data');

    return {
      baseCents,
      saleCents,
      varies: payload.dataset.pcpVaries === '1',
    };
  }

  function renderPriceListRevealed(block, discountData, control) {
    const revealed = block.querySelector('.pcp-block__revealed');
    if (!revealed) return;

    const { baseCents, saleCents, varies } = discountData;
    const fromTemplate = control?.dataset.pcpF;
    const saleFormatted = formatMoney(saleCents);
    const compareFormatted = formatMoney(baseCents);
    const isLarge = block.closest('price-list.price-list--lg, .price-list--lg');
    const saleLabel = control?.dataset.pcpSaleLabel || 'Sale price';
    const regularLabel = control?.dataset.pcpRegularLabel || 'Regular price';

    revealed.innerHTML =
      '<sale-price class="pcp-price__slot pcp-price__slot--ready' +
      (isLarge ? ' text-lg' : '') +
      '" translate="no"><span class="sr-only">' +
      saleLabel +
      '</span>' +
      (varies ? fromLabel(fromTemplate, saleFormatted) : saleFormatted) +
      '</sale-price>' +
      '<compare-at-price class="pcp-price__slot pcp-price__slot--ready' +
      (isLarge ? ' text-lg' : '') +
      '" translate="no"><span class="sr-only">' +
      regularLabel +
      '</span>' +
      (varies ? fromLabel(fromTemplate, compareFormatted) : compareFormatted) +
      '</compare-at-price>';

    revealed.hidden = false;
    revealed.setAttribute('aria-hidden', 'false');
  }

  function renderCardRevealed(block, discountData) {
    const revealed = block.querySelector('.pcp-block__revealed');
    if (!revealed) return;

    const { baseCents, saleCents } = discountData;

    revealed.innerHTML =
      '<span class="cts-price--current pcp-price__slot pcp-price__slot--ready" translate="no">' +
      formatMoney(saleCents) +
      '</span><span class="cts-price--original pcp-price__slot pcp-price__slot--ready" translate="no">' +
      formatMoney(baseCents) +
      '</span>';

    revealed.hidden = false;
    revealed.setAttribute('aria-hidden', 'false');
  }

  function showRevealed(block, control, discountData) {
    cacheInitialHtml(block);

    const initial = block.querySelector('.pcp-block__initial');
    if (initial) {
      initial.replaceChildren();
      initial.hidden = true;
      initial.setAttribute('aria-hidden', 'true');
    }

    if (block.classList.contains('pcp-block--price-list')) {
      renderPriceListRevealed(block, discountData, control);
    } else if (block.classList.contains('pcp-block--card')) {
      renderCardRevealed(block, discountData);
    }

    block.classList.add('pcp-block--revealed');
    block.classList.remove('pcp-block--error');
    setControlState(block, true);
  }

  function hideRevealed(block) {
    const initial = block.querySelector('.pcp-block__initial');
    const revealed = block.querySelector('.pcp-block__revealed');
    const cachedHtml = initialHtmlCache.get(block);

    clearRevealed(revealed);

    if (initial && cachedHtml) {
      initial.innerHTML = cachedHtml;
      initial.hidden = false;
      initial.removeAttribute('aria-hidden');
    }

    block.classList.remove('pcp-block--revealed', 'pcp-block--loading', 'pcp-block--error');
    setControlState(block, false);
  }

  async function revealBlock(block, control) {
    if (block.classList.contains('pcp-block--loading')) return;

    cacheInitialHtml(block);
    block.classList.add('pcp-block--loading');
    setControlsDisabled(block, true);

    let discountData = discountCache.get(block);

    try {
      if (!discountData) {
        discountData = await fetchDiscountData(control.dataset.pcpHandle);
        discountCache.set(block, discountData);
      }
    } catch (error) {
      block.classList.add('pcp-block--error');
      block.classList.remove('pcp-block--loading');
      setControlsDisabled(block, false);
      setControlState(block, false);
      return;
    }

    showRevealed(block, control, discountData);
    block.classList.remove('pcp-block--loading');
    setControlsDisabled(block, false);
  }

  async function toggleBlock(block, button) {
    if (block.classList.contains('pcp-block--loading')) return;

    if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
      return;
    }

    await revealBlock(block, button);
  }

  function onClick(event) {
    const button = event.target.closest('.pcp-reveal-btn');
    if (!button || button.disabled) return;

    const block = button.closest('.pcp-block');
    if (!block) return;

    event.preventDefault();
    toggleBlock(block, button);
  }

  function onChange(event) {
    const checkbox = event.target;
    if (!checkbox.matches('.pcp-reveal-checkbox') || checkbox.disabled) return;

    const block = checkbox.closest('.pcp-block');
    if (!block) return;

    if (checkbox.checked) {
      if (!block.classList.contains('pcp-block--revealed')) {
        revealBlock(block, checkbox);
      }
    } else if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
    }
  }

  function syncControlsOnResize() {
    document.querySelectorAll('.pcp-block--revealed[data-pcp-id]').forEach((block) => {
      setControlState(block, true);
    });
  }

  function initBlocks(root) {
    (root || document).querySelectorAll('.pcp-block[data-pcp-id]').forEach((block) => {
      cacheInitialHtml(block);

      const revealed = block.querySelector('.pcp-block__revealed');
      if (revealed) {
        revealed.hidden = true;
        revealed.setAttribute('aria-hidden', 'true');
        revealed.replaceChildren();
      }

      setControlState(block, false);
    });
  }

  function init() {
    initBlocks();
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);

    if (window.matchMedia) {
      const mq = window.matchMedia(DESKTOP_MQ);
      if (mq.addEventListener) {
        mq.addEventListener('change', syncControlsOnResize);
      } else if (mq.addListener) {
        mq.addListener(syncControlsOnResize);
      }
    }

    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node.parentElement || node);
          } else if (node.querySelector?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node);
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
